import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReviewTabs from './ReviewTabs';
import ReviewPrNowModal from './ReviewPrNowModal';

const STATUS_COLORS = {
  success: 'var(--success)', failed: 'var(--danger)', running: 'var(--warning)',
  partial: 'var(--warning)', cancelled: 'var(--text-muted)', interrupted: 'var(--danger)',
};

function StatusBadge({ run }) {
  const stopping = run.status === 'running' && run.cancel_requested;
  const label = stopping ? 'stopping' : (run.status || '—');
  const c = stopping ? 'var(--danger)' : (STATUS_COLORS[run.status] || 'var(--text-muted)');
  return <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: `${c}22`, color: c, textTransform: 'capitalize' }}>{label}</span>;
}

function fmtDate(v) { if (!v) return '—'; try { return new Date(v).toLocaleString(); } catch { return v; } }

// Progress cell: "completed / selected" once a run knows its plan, else reviewed.
function prsCell(r) {
  if (r.prs_selected) return `${r.prs_completed ?? 0} / ${r.prs_selected}`;
  return r.prs_reviewed ?? 0;
}

export default function ReviewRunsList() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const configId = params.get('config_id');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState({});
  const [reviewPr, setReviewPr] = useState(false);
  const timerRef = useRef(null);

  const fetchRuns = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const q = configId ? `?config_id=${configId}` : '';
      const res = await axios.get(`/code-review/runs${q}`);
      setRuns(res.data.data || []);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load runs'); }
    finally { if (!silent) setLoading(false); }
  }, [configId]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Poll while any run is active so progress + status update live.
  const hasActive = runs.some(r => r.status === 'running');
  useEffect(() => {
    if (!hasActive) return undefined;
    timerRef.current = setInterval(() => fetchRuns(true), 5000);
    return () => clearInterval(timerRef.current);
  }, [hasActive, fetchRuns]);

  async function stopRun(e, id) {
    e.stopPropagation();
    if (!window.confirm('Stop this run? It halts after the current PR finishes; PRs already reviewed are kept.')) return;
    setStopping(s => ({ ...s, [id]: true }));
    try {
      await axios.post(`/code-review/runs/${id}/stop`);
      fetchRuns(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to stop run');
    } finally {
      setStopping(s => ({ ...s, [id]: false }));
    }
  }

  return (
    <div>
      <div className="page-header">
        <h2>Review Runs</h2>
        <p>Every scan execution, with PRs selected/completed, findings, tokens and cost.</p>
      </div>
      <div className="page-body">
        <ReviewTabs />
        <div className="toolbar">
          <div className="spacer" />
          {hasActive && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 10 }}>● live — refreshing every 5s</span>}
          <button className="btn btn-primary" style={{ marginRight: 8 }} onClick={() => setReviewPr(true)}>Review a PR now…</button>
          <button className="btn btn-secondary" onClick={() => fetchRuns()} disabled={loading}>Refresh</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
          : runs.length === 0 ? <div className="empty-state"><h3>No runs yet</h3><p>Review a single PR with the button above, or trigger a scan from the Configs tab.</p></div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                    <th style={{ padding: '8px 10px' }}>Started</th>
                    <th style={{ padding: '8px 10px' }}>Config</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                    <th style={{ padding: '8px 10px' }}>Mode</th>
                    <th style={{ padding: '8px 10px' }} title="Completed / selected">PRs (done/selected)</th>
                    <th style={{ padding: '8px 10px' }}>Findings</th>
                    <th style={{ padding: '8px 10px' }}>Tokens</th>
                    <th style={{ padding: '8px 10px' }}>Cost</th>
                    <th style={{ padding: '8px 10px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 }}
                      onClick={() => navigate(`/code-review/runs/${r.id}`)}>
                      <td style={{ padding: '10px' }}>{fmtDate(r.started_at)}</td>
                      <td style={{ padding: '10px' }}>{r.config_name || `#${r.review_config_id}`}</td>
                      <td style={{ padding: '10px' }}><StatusBadge run={r} /></td>
                      <td style={{ padding: '10px' }}>{r.post_mode}</td>
                      <td style={{ padding: '10px' }}>{prsCell(r)}</td>
                      <td style={{ padding: '10px' }}>{r.live_findings ?? r.findings_count}</td>
                      <td style={{ padding: '10px' }}>{Number(r.live_tokens ?? (r.input_tokens + r.output_tokens)).toLocaleString()}</td>
                      <td style={{ padding: '10px' }}>${Number(r.live_cost ?? r.cost_usd).toFixed(4)}</td>
                      <td style={{ padding: '10px' }}>
                        {r.status === 'running' && (
                          <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--danger)' }}
                            disabled={stopping[r.id] || r.cancel_requested}
                            onClick={e => stopRun(e, r.id)}>
                            {r.cancel_requested ? 'Stopping…' : 'Stop'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {reviewPr && <ReviewPrNowModal onClose={() => setReviewPr(false)} defaultConfigId={configId || ''} />}
    </div>
  );
}
